/**
 * @file HR API
 * 직원 현황, 근태 관리, 휴가 관리, 급여 관리, 조직도, 결재 연동 등
 * 인사 관련 전반을 처리합니다.
 */

import { api } from "../../../util/api";
import { formatKrw } from "../../../util/numberFormat";

const API_BASE = process.env.REACT_APP_API_BASE_URL;

export const hrApi = {
    // 직원 현황
    /** HR 직원 통계 조회 (재직·휴직·퇴직 인원 수 등) */
    getStats:     ()              => api.get(`${API_BASE}/hr/employees/stats`),

    /** HR 직원 목록 조회 (페이지네이션)
     * @param {object} [params={}] - 필터 파라미터 (status, deptId, search, page, size)
     */
    getEmployees: (params = {})   => api.get(`${API_BASE}/hr/employees`, params),

    /** 직원 재직 상태 변경
     * @param {number} id - 직원 ID
     * @param {string} statusName - 변경할 상태 (ACTIVE · LEAVE · RETIRED)
     */
    updateStatus: (id, statusName)  =>
        api.patch(`${API_BASE}/hr/employees/${id}/status`, null, { params: { statusName } }),

    /** 직원 전화번호 수정
     * @param {number} id - 직원 ID
     * @param {string} phoneNumber - 새 전화번호
     */
    updatePhone:  (id, phoneNumber) =>
        api.patch(`${API_BASE}/hr/employees/${id}/phone`, null, { params: { phoneNumber } }),

    // 근태
    /** 근무 설정 조회 */
    getWorkSetting:          ()                              => api.get(`${API_BASE}/hr/attendance/work-setting`),

    /** 근무 설정 저장
     * @param {object} data - 근무 설정 데이터
     */
    saveWorkSetting:         (data)                          => api.put(`${API_BASE}/hr/attendance/work-setting`, data),

    /** 월별 근태 목록 조회
     * @param {number} year - 조회 연도
     * @param {number} month - 조회 월
     */
    getAttendance:           (year, month)                   => api.get(`${API_BASE}/hr/attendance`, { year, month }),

    /** 기간별 근태 목록 조회
     * @param {string} from - 시작일 (YYYY-MM-DD)
     * @param {string} to - 종료일 (YYYY-MM-DD)
     */
    getAttendanceByRange:    (from, to)                      => api.get(`${API_BASE}/hr/attendance/range`, { from, to }),

    /** 특정 직원의 월별 근태 조회
     * @param {number} employeeId - 직원 ID
     * @param {number} year - 조회 연도
     * @param {number} month - 조회 월
     */
    getAttendanceByEmployee: (employeeId, year, month)       =>
        api.get(`${API_BASE}/hr/attendance/employee/${employeeId}`, { year, month }),

    /** 근태 기록 저장
     * @param {object} data - 근태 데이터
     */
    saveAttendance:          (data)                          => api.post(`${API_BASE}/hr/attendance`, data),

    /** 근태 기록 삭제
     * @param {number} id - 근태 기록 ID
     */
    deleteAttendance:        (id)                            => api.delete(`${API_BASE}/hr/attendance/${id}`),

    // 휴가
    /** 전체 직원 휴가 부여 목록 조회 */
    getLeaveAllowances:   ()               => api.get(`${API_BASE}/hr/leave`),

    /** 특정 직원의 휴가 부여 내역 조회
     * @param {number} employeeId - 직원 ID
     */
    getLeaveAllowance:    (employeeId)      => api.get(`${API_BASE}/hr/leave/employee/${employeeId}`),

    /** 휴가 부여 저장 (생성 또는 수정)
     * @param {object} data - 휴가 부여 데이터
     */
    saveLeaveAllowance:   (data)            => api.post(`${API_BASE}/hr/leave`, data),

    /** 휴가 부여 내역 삭제
     * @param {number} id - 휴가 부여 ID
     */
    deleteLeaveAllowance: (id)              => api.delete(`${API_BASE}/hr/leave/${id}`),

    /** 특정 직원의 연도별 휴가 사용 현황 조회
     * @param {number} employeeId - 직원 ID
     * @param {number} year - 조회 연도
     */
    getLeaveUsage:        (employeeId, year) =>
        api.get(`${API_BASE}/hr/leave/usage`, { employeeId, year }),

    /** 전체 직원 연도별 휴가 사용 현황 조회
     * @param {number} year - 조회 연도
     */
    getAllLeaveUsage:      (year)            => api.get(`${API_BASE}/hr/leave/usage/all`, { year }),

    /** 직원별 대체휴무 적립 근거 (승인된 휴일근무 결재) */
    getCompGrants:        (employeeId, year) =>
        api.get(`${API_BASE}/hr/leave/comp-grants`, { employeeId, year }),

    /** 연도별 휴가 결재 전체 목록 조회
     * @param {number} year - 조회 연도
     */
    getVacationApprovals: (year)            => api.get(`${API_BASE}/hr/leave/approvals/all`, { year }),

    /** 결재 대기 중인 휴가 신청 목록 조회 */
    getPendingVacations:  ()                => api.get(`${API_BASE}/hr/leave/approvals/pending`),

    // 휴가 결재의 승인/반려는 공통 결재 시스템(approvalApi → /api/approval/{id}/approve|reject,
    // ApprovalIntegrationPanel·ApprovalDetail)으로 처리한다. 과거 여기 있던 approveVacation/rejectVacation 은
    // 존재하지 않는 /hr/leave/approvals/{id}/approve|reject 를 가리키던 미사용 고아 함수라 제거했다.

    // 급여
    /** 월별 급여 목록 조회 (페이지네이션)
     * @param {number} year - 조회 연도
     * @param {number} month - 조회 월
     * @param {number} [page=0] - 페이지 번호 (0-based)
     * @param {number} [size=20] - 페이지 크기
     */
    getSalaries: (year, month, page = 0, size = 20) => api.get(`${API_BASE}/hr/salary`, { year, month, page, size }),

    /** 특정 직원의 급여 이력 조회
     * @param {number} employeeId - 직원 ID
     */
    getSalaryByEmployee: (employeeId)       => api.get(`${API_BASE}/hr/salary/employee/${employeeId}`),

    /** 미지급(연체) 급여 목록 조회 */
    getOverdueSalaries:  ()                 => api.get(`${API_BASE}/hr/salary/overdue`),

    /** 급여 저장 (생성 또는 수정)
     * @param {object} data - 급여 데이터
     */
    saveSalary:          (data)             => api.post(`${API_BASE}/hr/salary`, data),

    /** 급여 수당 추가
     * @param {number} salaryId - 급여 ID
     * @param {object} data - 수당 데이터
     */
    addAllowance:        (salaryId, data)   => api.post(`${API_BASE}/hr/salary/${salaryId}/allowance`, data),

    /** 급여 지급 처리
     * @param {number} salaryId - 급여 ID
     * @param {object} data - 지급 처리 데이터
     */
    recordPayment:       (salaryId, data)   => api.post(`${API_BASE}/hr/salary/${salaryId}/pay`, data),

    /** 특정 월 전체 급여 일괄 지급 처리
     * @param {number} year - 연도
     * @param {number} month - 월
     */
    payAll:              (year, month)      =>
        api.post(`${API_BASE}/hr/salary/pay-all`, null, { params: { year, month } }),

    /** 급여 삭제
     * @param {number} id - 급여 ID
     */
    deleteSalary:        (id)               => api.delete(`${API_BASE}/hr/salary/${id}`),

    // 결재 연동
    /** HR 관련 결재 목록 조회 (휴가 결재 등) */
    getHrApprovals: () => api.get(`${API_BASE}/hr/leave/hr-approvals`),

    // 공통
    /** 부서 목록 조회 */
    getDepartments: () => api.get(`${API_BASE}/employees/departments`),

    // AI 사용 내역 (인사팀 전용 — 대화 내용은 미포함)
    /** 기간 + 부서/직원 필터로 전 직원 챗봇 사용 통계 조회.
     * @param {{from:string, to:string, deptId?:number, employeeId?:number}} params
     */
    getAiUsage: ({ from, to, deptId, employeeId } = {}) => {
        const p = { from, to };
        if (deptId != null)     p.deptId     = deptId;
        if (employeeId != null) p.employeeId = employeeId;
        return api.get(`${API_BASE}/admin/chatbot/usage`, p);
    },

    /** 특정 직원의 일자별 챗봇 사용 추이 (그래프용).
     * @param {number} employeeId - 직원 ID
     * @param {string} from - 시작일 (YYYY-MM-DD)
     * @param {string} to - 종료일 (YYYY-MM-DD)
     */
    getAiUsageTimeline: (employeeId, from, to) =>
        api.get(`${API_BASE}/admin/chatbot/usage/${employeeId}/timeline`, { from, to }),

    // Audit Log (감사 로그) — HR_EMPLOYEE_READ 권한 필요
    /** 감사 로그 동적 필터 조회.
     * @param {object} params - { actorEmployeeId?, domain?, action?, result?, from?, to?, page?, size? }
     */
    getAuditLogs: (params = {}) => {
        const p = {};
        if (params.actorEmployeeId != null) p.actorEmployeeId = params.actorEmployeeId;
        if (params.domain)                  p.domain = params.domain;
        if (params.action)                  p.action = params.action;
        if (params.result)                  p.result = params.result;
        if (params.from)                    p.from = params.from;
        if (params.to)                      p.to = params.to;
        p.page = params.page ?? 0;
        p.size = params.size ?? 30;
        return api.get(`${API_BASE}/admin/audit/logs`, p);
    },

    /** 지금까지 기록된 도메인 목록 (필터 드롭다운). */
    getAuditDomains: () => api.get(`${API_BASE}/admin/audit/domains`),
};

export const fmtKRW = (n) => formatKrw(n);

export const STATUS_COLOR = {
    ACTIVE:  { bg: "#f0fdf4", color: "#16a34a" },
    LEAVE:   { bg: "#fffbeb", color: "#d97706" },
    RETIRED: { bg: "#f1f5f9", color: "#64748b" },
};

export const ATTENDANCE_COLOR = {
    NORMAL:        { bg: "#f0fdf4", color: "#16a34a", label: "정상" },
    LATE:          { bg: "#fff7ed", color: "#ea580c", label: "지각" },
    EARLY_LEAVE:   { bg: "#fef9c3", color: "#ca8a04", label: "조퇴" },
    ABSENT:        { bg: "#fef2f2", color: "#dc2626", label: "결근" },
    VACATION:      { bg: "#eff6ff", color: "#2563eb", label: "휴가" },
    BUSINESS_TRIP: { bg: "#eff6ff", color: "#2563eb", label: "외근" },
    HALF_DAY:      { bg: "#fdf4ff", color: "#a21caf", label: "반차" },
};

export const SALARY_STATUS_COLOR = {
    SCHEDULED: { bg: "#eff6ff", color: "#2563eb", label: "지급예정" },
    PARTIAL:   { bg: "#fff7ed", color: "#ea580c", label: "일부지급" },
    PAID:      { bg: "#f0fdf4", color: "#16a34a", label: "지급완료" },
    OVERDUE:   { bg: "#fef2f2", color: "#dc2626", label: "미지급" },
};
